import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBill, faWallet, faClock, faTimes, faFile, faHistory, faExchangeAlt,
  faPiggyBank, faCheck, faXmark, faArrowRight, faReceipt, faSnowflake, faVault,
  faRotate, faPlus, faCopy, faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useWallet } from '../contexts/WalletContext';
import { bankList } from '../utils/bankList';
import bankLogos from 'thai-banks-logo/banks-logo.json';
import dayjs from 'dayjs';
import { fmtMoney } from '../utils/formatMoney';
import DataTable, { Column } from '../components/shared/DataTable';
import {
  UiPageHeader, UiButton, UiModal, UiInput, StatusBadge,
} from '../components/ui';
import type { StatusDef } from '../components/ui';

interface PayoutTransfer {
  id: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  payout_balance_before: number;
  payout_balance_after: number;
  direction: 'to_payout' | 'to_main' | 'topup';
  note: string | null;
  created_at: string;
  created_by_type: string;
  created_by_name: string | null;
}

interface MerchantTopup {
  id: string;
  amount: number;
  fee_amount?: number;
  net_amount?: number;
  bank_name?: string;
  bank_number?: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  amount_mismatch?: boolean;
  created_at: string;
  paid_at?: string | null;
}

interface FreezeHistory {
  id: string;
  freeze_date: string;
  status: 'frozen' | 'unfrozen';
  unfrozen_at: string | null;
  deduction_percentage: number | null;
  total_returned: number | null;
  total_deducted: number | null;
  payin_amount: number;
  payout_amount: number;
  total_frozen: number;
  returned_amount: number | null;
  deducted_amount: number | null;
  created_at: string;
}

interface Transaction {
  id: string;
  transaction_type: string;
  amount: number;
  description: string;
  created_at: string;
  fee_amount: number;
  status: string;
  slip_url?: string;
}

const TX_STATUS_MAP: Record<string, StatusDef> = {
  completed: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheck, label: 'completed' },
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faClock, label: 'pending' },
  approved:  { bg: 'bg-green-100', text: 'text-green-700', icon: faCheck, label: 'approved' },
  rejected:  { bg: 'bg-red-100', text: 'text-red-600', icon: faTimes, label: 'rejected' },
};

const bahtFmt = (n: number) => `฿${n.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;

/* Glass sub-stat tile for gradient (white-text) wallet cards */
const GlassStat: React.FC<{ icon?: any; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="rounded-xl bg-white/10 px-3 py-2.5 ring-1 ring-inset ring-white/15 backdrop-blur-sm">
    <p className="text-[11px] font-medium text-white/70 flex items-center gap-1.5 mb-1">
      {icon && <FontAwesomeIcon icon={icon} className="text-[10px]" />}{label}
    </p>
    <p className="text-base font-bold tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
  </div>
);

const WalletPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { wallet, loadWallet } = useWallet();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState('');

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferring, setTransferring] = useState(false);

  const [showTransferToMainModal, setShowTransferToMainModal] = useState(false);
  const [transferToMainAmount, setTransferToMainAmount] = useState('');
  const [transferToMainNote, setTransferToMainNote] = useState('');
  const [transferringToMain, setTransferringToMain] = useState(false);

  // Payout top-up (เติมเงินเข้า payout ด้วยสลิป)
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupPhase, setTopupPhase] = useState<1 | 2>(1);
  const [topupBankName, setTopupBankName] = useState('');
  const [topupBankNumber, setTopupBankNumber] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupCreating, setTopupCreating] = useState(false);
  const [topupResult, setTopupResult] = useState<any>(null);
  const [topupSlip, setTopupSlip] = useState<string>('');
  const [topupUploading, setTopupUploading] = useState(false);
  const [topupError, setTopupError] = useState('');

  const [payoutTransfers, setPayoutTransfers] = useState<PayoutTransfer[]>([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);

  const [payoutTopupsList, setPayoutTopupsList] = useState<MerchantTopup[]>([]);
  const [loadingTopupsList, setLoadingTopupsList] = useState(false);
  // ยอดรวมจาก backend (ครบทุกแถว ไม่ใช่แค่ 50 แถวที่โหลด) — backend เก่าไม่มี summary → null
  const [topupSummary, setTopupSummary] = useState<{
    paid_count: number; total_amount: number; total_fee: number; total_net: number; pending_count: number;
  } | null>(null);
  // เรท fee เติม payout = fee_percentage (เรทเดียวกับฝาก) — ใช้ทำ preview ใน modal
  const [feePercentage, setFeePercentage] = useState<number | null>(null);

  const [freezeHistory, setFreezeHistory] = useState<FreezeHistory[]>([]);
  const [loadingFreezeHistory, setLoadingFreezeHistory] = useState(false);

  const [usdtAddress, setUsdtAddress] = useState<string | null>(null);
  const [showUsdtPopup, setShowUsdtPopup] = useState(false);

  // Auto Transfer
  const [autoTransferSettings, setAutoTransferSettings] = useState<{
    auto_transfer_enabled: boolean;
    auto_transfer_threshold: number;
    auto_transfer_max_payout: number;
  } | null>(null);
  const [autoTransferLogs, setAutoTransferLogs] = useState<any[]>([]);
  const [loadingAutoTransfer, setLoadingAutoTransfer] = useState(false);
  const [savingAutoTransfer, setSavingAutoTransfer] = useState(false);
  const [editThreshold, setEditThreshold] = useState('');
  const [editMaxPayout, setEditMaxPayout] = useState('');

  const loadTransactions = useCallback(async () => {
    try {
      // ดึง Settlement + Safe Wallet transactions พร้อมกัน
      const [settlementsRes, safeRes] = await Promise.all([
        merchantApi.get('/merchants/wallet/settlements'),
        merchantApi.get('/merchants/wallet/safe-wallet/transactions?limit=20').catch(() => ({ data: { data: [] } })),
      ]);

      const settlementTransactions = (settlementsRes.data.data || []).map((s: any) => ({
        id: s.id,
        transaction_type: 'debit',
        amount: s.amount,
        description: `${t('wallet.withdrawal')} - ${s.status === 'completed' ? t('wallet.withdrawalCompleted') : s.status === 'pending' ? t('wallet.withdrawalPending') : t('wallet.withdrawalRejected')}`,
        created_at: s.created_at,
        fee_amount: 0,
        status: s.status,
        slip_url: s.slip_url,
      }));

      const safeTransactions = (safeRes.data.data || []).map((s: any) => {
        let description = '';
        if (s.transaction_type === 'withdraw') {
          description = t('wallet.safeWithdrawDesc', {
            usdt: parseFloat(s.usdt_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
            thb: parseFloat(s.net_thb).toLocaleString(),
            fee: parseFloat(s.fee_amount).toLocaleString(),
          });
        } else if (s.transaction_type === 'settlement') {
          description = t('wallet.safeSettlementDesc', {
            usdt: parseFloat(s.usdt_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
          });
        } else {
          description = t('wallet.safeDepositDesc', {
            thb: parseFloat(s.amount_thb).toLocaleString(),
            usdt: parseFloat(s.usdt_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }),
            fee: parseFloat(s.fee_amount).toLocaleString(),
          });
        }
        return {
          id: `safe-${s.id}`,
          transaction_type: 'debit',
          amount: s.amount_thb,
          description,
          created_at: s.created_at,
          fee_amount: s.fee_amount,
          status: s.status === 'settled' ? 'completed' : s.status,
          slip_url: null,
        };
      });

      // รวมทั้ง 2 ประเภท แล้ว sort ตามวันที่
      const allTransactions = [...settlementTransactions, ...safeTransactions]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading transactions');
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadPayoutTransfers = useCallback(async () => {
    try {
      setLoadingTransfers(true);
      const response = await merchantApi.get('/merchants/wallet/payout-transfers?limit=20');
      if (response.data.success) {
        setPayoutTransfers(response.data.data.transfers || []);
      }
    } catch (error) {
      console.error('Error loading payout transfers');
    } finally {
      setLoadingTransfers(false);
    }
  }, []);

  const loadPayoutTopups = useCallback(async () => {
    try {
      setLoadingTopupsList(true);
      const response = await merchantApi.get('/merchants/wallet/payout-topups?limit=50');
      if (response.data.success) {
        setPayoutTopupsList(response.data.data || []);
        setTopupSummary(response.data.summary || null);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingTopupsList(false);
    }
  }, []);

  const topupColumns: Column<MerchantTopup>[] = [
    {
      key: 'created_at', header: t('common.date'), render: (r) => (
        <div className="text-xs">
          <div className="text-gray-700 dark:text-slate-200">{dayjs(r.created_at).format('DD/MM/YYYY')}</div>
          <div className="text-gray-400 dark:text-slate-500">{dayjs(r.created_at).format('HH:mm')}</div>
        </div>
      )
    },
    {
      key: 'amount', header: t('wallet.topup.amountLabel'), render: (r) => (
        <div className="text-sm">
          <div className="font-bold" style={{ color: 'var(--theme-accent)' }}>฿{fmtMoney(r.amount)}</div>
          {r.fee_amount ? <div className="text-[11px] text-amber-600 dark:text-amber-400">fee -฿{fmtMoney(r.fee_amount)}</div> : null}
        </div>
      )
    },
    {
      key: 'net_amount', header: t('wallet.topup.toPayout'), render: (r) => (
        r.net_amount ? <strong className="text-emerald-600 dark:text-emerald-400">฿{fmtMoney(r.net_amount)}</strong> : <span className="text-gray-300 dark:text-slate-600">-</span>
      )
    },
    {
      key: 'sender', header: t('wallet.topup.senderBank'), render: (r) => (
        <div className="text-xs">
          <div className="text-gray-700 dark:text-slate-200">{r.bank_name || '-'}</div>
          <div className="text-gray-400 dark:text-slate-500 font-mono">{r.bank_number || ''}</div>
        </div>
      )
    },
    {
      key: 'status', header: t('common.status'), render: (r) => {
        const cls = r.status === 'paid' ? 'text-green-600 dark:text-green-400' : r.status === 'pending' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500';
        const labels: Record<string, string> = { pending: t('wallet.topup.stPending'), paid: t('wallet.topup.stPaid'), expired: t('wallet.topup.stExpired'), cancelled: t('wallet.topup.stCancelled') };
        return <span className={`text-xs font-semibold ${cls}`}>{labels[r.status] || r.status}{r.amount_mismatch ? ' ⚠' : ''}</span>;
      }
    },
  ];

  const loadFreezeHistory = useCallback(async () => {
    try {
      setLoadingFreezeHistory(true);
      const response = await merchantApi.get('/merchants/wallet/freeze-history?limit=20');
      if (response.data.success) {
        setFreezeHistory(response.data.data.history || []);
      }
    } catch (error) {
      console.error('Error loading freeze history');
    } finally {
      setLoadingFreezeHistory(false);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/profile');
      const addr = response.data.data?.usdt_address || '';
      setUsdtAddress(addr);
      const fp = response.data.data?.fee_percentage;
      setFeePercentage(fp != null ? parseFloat(fp) : null);
    } catch {
      // silent
    }
  }, []);

  const loadAutoTransferSettings = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/wallet/auto-transfer-settings');
      if (response.data.success) {
        const s = response.data.data;
        setAutoTransferSettings(s);
        setEditThreshold(String(s.auto_transfer_threshold || ''));
        setEditMaxPayout(String(s.auto_transfer_max_payout || ''));
      }
    } catch {
      // silent
    }
  }, []);

  const loadAutoTransferLogs = useCallback(async () => {
    try {
      setLoadingAutoTransfer(true);
      const response = await merchantApi.get('/merchants/wallet/auto-transfer-logs?limit=20');
      if (response.data.success) {
        setAutoTransferLogs(response.data.data.logs || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingAutoTransfer(false);
    }
  }, []);

  useEffect(() => {
    const hasFrozen = !!wallet?.is_frozen || freezeHistory.some(f => f.status === 'frozen');
    if (usdtAddress !== null && hasFrozen && !usdtAddress) {
      setShowUsdtPopup(true);
    }
  }, [usdtAddress, wallet, freezeHistory]);

  useEffect(() => {
    loadTransactions();
    loadPayoutTransfers();
    loadPayoutTopups();
    loadFreezeHistory();
    loadProfile();
    loadAutoTransferSettings();
    loadAutoTransferLogs();
    const interval = setInterval(() => {
      loadWallet();
      loadTransactions();
      loadPayoutTransfers();
      loadFreezeHistory();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadWallet, loadTransactions, loadPayoutTransfers, loadPayoutTopups, loadFreezeHistory, loadProfile, loadAutoTransferSettings, loadAutoTransferLogs]);

  const formatSlipUrl = (slipUrl: string) => {
    if (!slipUrl) return '';
    if (!slipUrl.startsWith('http') && !slipUrl.startsWith('data:')) {
      return `data:image/png;base64,${slipUrl}`;
    }
    return slipUrl;
  };

  const handleViewSlip = (slipUrl: string) => {
    const formattedUrl = formatSlipUrl(slipUrl);
    setSelectedSlipUrl(formattedUrl);
    setShowSlipModal(true);
  };

  const handleSaveAutoTransfer = async () => {
    setSavingAutoTransfer(true);
    try {
      const payload: any = {
        auto_transfer_enabled: autoTransferSettings?.auto_transfer_enabled || false,
      };
      if (editThreshold) payload.auto_transfer_threshold = parseFloat(editThreshold);
      if (editMaxPayout) payload.auto_transfer_max_payout = parseFloat(editMaxPayout);

      const response = await merchantApi.put('/merchants/wallet/auto-transfer-settings', payload);
      if (response.data.success) {
        toast.success(t('wallet.autoTransfer.saveSuccess'));
        loadAutoTransferSettings();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSavingAutoTransfer(false);
    }
  };

  const handleTransferToPayout = async () => {
    const amount = parseFloat(transferAmount);

    if (!amount || amount <= 0) {
      toast.error(t('wallet.validAmountRequired'));
      return;
    }

    const availableBalance = (isFrozen ? wallet?.available_balance : wallet?.balance) ?? 0;
    if (amount > availableBalance) {
      toast.error(t('wallet.insufficientBalance', { balance: `฿${availableBalance?.toLocaleString()}` }));
      return;
    }

    try {
      setTransferring(true);
      const res = await merchantApi.post('/merchants/wallet/transfer-to-payout', {
        amount,
        note: transferNote || undefined,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setShowTransferModal(false);
        setTransferAmount('');
        setTransferNote('');
        loadWallet();
        loadPayoutTransfers();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('wallet.transferError'));
    } finally {
      setTransferring(false);
    }
  };

  const resetTopup = () => {
    setShowTopupModal(false); setTopupPhase(1);
    setTopupBankName(''); setTopupBankNumber(''); setTopupAmount(''); setTopupNote('');
    setTopupResult(null); setTopupSlip(''); setTopupError('');
  };
  const copyTopupText = (txt: string) => { navigator.clipboard?.writeText(txt); toast.success(t('wallet.topup.copied')); };
  const handleCreateTopup = async () => {
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { setTopupError(t('wallet.topup.errAmount')); return; }
    if (!topupBankName) { setTopupError(t('wallet.topup.errBank')); return; }
    if (!topupBankNumber.trim()) { setTopupError(t('wallet.topup.errAccount')); return; }
    setTopupError(''); setTopupCreating(true);
    try {
      const res = await merchantApi.post('/merchants/wallet/payout-topups', {
        amount: amt, bank_name: topupBankName, bank_number: topupBankNumber.trim(), note: topupNote || undefined,
      });
      if (res.data.success) { setTopupResult(res.data.data); setTopupPhase(2); }
      else setTopupError(res.data.message || t('wallet.topup.errGeneric'));
    } catch (e: any) { setTopupError(e.response?.data?.message || t('wallet.topup.errGeneric')); }
    finally { setTopupCreating(false); }
  };
  const handleTopupSlipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setTopupSlip(reader.result as string);
    reader.readAsDataURL(file);
  };
  const handleUploadTopupSlip = async () => {
    if (!topupSlip) { setTopupError(t('wallet.topup.errSlip')); return; }
    if (!topupResult?.id) { setTopupError(t('wallet.topup.errNotFound')); return; }
    setTopupError(''); setTopupUploading(true);
    try {
      const res = await merchantApi.post(`/merchants/wallet/payout-topups/${topupResult.id}/upload-slip`, { slip_base64: topupSlip });
      if (res.data.success) {
        toast.success(res.data.message || t('wallet.topup.success'));
        resetTopup(); loadWallet(); loadPayoutTransfers(); loadPayoutTopups();
      } else setTopupError(res.data.message || t('wallet.topup.errVerify'));
    } catch (e: any) { setTopupError(e.response?.data?.message || t('wallet.topup.errVerify')); }
    finally { setTopupUploading(false); }
  };

  const handleTransferToMain = async () => {
    const amount = parseFloat(transferToMainAmount);

    if (!amount || amount <= 0) {
      toast.error(t('wallet.validAmountRequired'));
      return;
    }

    if (amount > (wallet?.payout_balance || 0)) {
      toast.error(t('wallet.insufficientPayout', { balance: (wallet?.payout_balance || 0).toLocaleString() }));
      return;
    }

    if (!window.confirm(t('wallet.confirmTransferToMain', { amount: amount.toLocaleString() }))) {
      return;
    }

    try {
      setTransferringToMain(true);
      const res = await merchantApi.post('/merchants/wallet/transfer-payout-to-main', {
        amount,
        note: transferToMainNote || undefined,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setShowTransferToMainModal(false);
        setTransferToMainAmount('');
        setTransferToMainNote('');
        loadWallet();
        loadPayoutTransfers();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('wallet.transferToMainError'));
    } finally {
      setTransferringToMain(false);
    }
  };

  // Columns for Payout Transfers Table
  const transferColumns: Column<PayoutTransfer>[] = [
    {
      key: 'icon',
      header: '',
      width: '60px',
      render: (transfer) => (
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{
          background: transfer.direction === 'to_main'
            ? 'var(--theme-gradient-warning)'
            : 'var(--theme-accent-gradient)',
        }}>
          <FontAwesomeIcon icon={faExchangeAlt} />
        </div>
      ),
    },
    {
      key: 'description',
      header: t('wallet.detail'),
      render: (transfer) => (
        <div>
          <p className="font-semibold text-gray-800 dark:text-slate-100">
            {transfer.direction === 'to_main' ? 'Payout → Main Wallet' : transfer.direction === 'topup' ? t('wallet.topup.historyLabel') : t('wallet.transferToPW')}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {new Date(transfer.created_at).toLocaleString('th-TH')}
            {transfer.created_by_name && (
              <span className={`ml-2 ${transfer.direction === 'to_main' ? 'text-orange-600 dark:text-orange-400' : 'text-indigo-600 dark:text-indigo-400'}`}>{t('wallet.by')} {transfer.created_by_name}</span>
            )}
          </p>
          {transfer.note && <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{transfer.note}</p>}
        </div>
      ),
    },
    {
      key: 'balance_change',
      header: t('wallet.balanceChange'),
      render: (transfer) => (
        <div className="text-xs text-gray-500 dark:text-slate-400">
          <div>Main: ฿{transfer.balance_before.toLocaleString()} <FontAwesomeIcon icon={faArrowRight} className="mx-1" /> ฿{transfer.balance_after.toLocaleString()}</div>
          <div>Payout: ฿{transfer.payout_balance_before.toLocaleString()} <FontAwesomeIcon icon={faArrowRight} className="mx-1" /> ฿{transfer.payout_balance_after.toLocaleString()}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('common.amount'),
      sortable: true,
      render: (transfer) => (
        <div className={`text-lg font-bold ${transfer.direction === 'to_main' ? 'text-orange-600 dark:text-orange-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
          {transfer.direction === 'to_main' ? '↩' : '+'} ฿{transfer.amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </div>
      ),
    },
  ];

  // Columns for Transactions Table
  const transactionColumns: Column<Transaction>[] = [
    {
      key: 'icon',
      header: '',
      width: '60px',
      render: (tx) => {
        const isSafe = tx.id.startsWith('safe-');
        if (isSafe) {
          return (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
              style={{ background: 'var(--theme-secondary-gradient)' }}>
              <FontAwesomeIcon icon={faVault} />
            </div>
          );
        }
        return (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
            tx.status === 'completed' ? 'bg-green-500' :
            tx.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'
          }`}>
            {tx.status === 'completed' ? <FontAwesomeIcon icon={faCheck} /> :
             tx.status === 'pending' ? <FontAwesomeIcon icon={faClock} /> : <FontAwesomeIcon icon={faTimes} />}
          </div>
        );
      },
    },
    {
      key: 'description',
      header: t('wallet.detail'),
      render: (tx) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-slate-100">{tx.description}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-gray-500 dark:text-slate-400">{new Date(tx.created_at).toLocaleString('th-TH')}</span>
            <StatusBadge status={tx.status} statusMap={TX_STATUS_MAP} />
          </div>
        </div>
      ),
    },
    {
      key: 'slip',
      header: t('common.slip'),
      render: (tx) => (
        tx.slip_url ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewSlip(tx.slip_url!);
            }}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faFile} /> {t('common.viewSlip')}
          </button>
        ) : <span className="text-gray-400 dark:text-slate-500">-</span>
      ),
    },
    {
      key: 'amount',
      header: t('common.amount'),
      sortable: true,
      render: (tx) => (
        <div className="text-lg font-bold text-red-600 dark:text-red-400">
          -฿{tx.amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </div>
      ),
    },
  ];

  // Columns for Freeze History Table
  const freezeHistoryColumns: Column<FreezeHistory>[] = [
    {
      key: 'icon',
      header: '',
      width: '60px',
      render: (record) => (
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
          record.status === 'frozen' ? 'bg-blue-500' : 'bg-green-500'
        }`}>
          <FontAwesomeIcon icon={faSnowflake} />
        </div>
      ),
    },
    {
      key: 'freeze_info',
      header: t('wallet.detail'),
      render: (record) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-slate-100">
            {record.status === 'frozen' ? t('wallet.frozenStatus') : t('wallet.unfrozenStatus')}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {new Date(record.freeze_date).toLocaleDateString('th-TH')}
            {record.status === 'unfrozen' && record.unfrozen_at && (
              <span className="ml-2">→ {new Date(record.unfrozen_at).toLocaleDateString('th-TH')}</span>
            )}
          </p>
          {record.status === 'unfrozen' && record.deduction_percentage !== null && (
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              {t('wallet.frozenDeduction', { pct: record.deduction_percentage, amount: record.deducted_amount?.toLocaleString('th-TH', { minimumFractionDigits: 2 }) })}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'amounts',
      header: t('common.details'),
      render: (record) => (
        <div className="text-xs text-gray-500 dark:text-slate-400">
          <div>{t('wallet.payinLabel')}: ฿{record.payin_amount?.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
          <div>{t('wallet.payoutLabel')}: ฿{record.payout_amount?.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
        </div>
      ),
    },
    {
      key: 'total_frozen',
      header: t('common.amount'),
      sortable: true,
      render: (record) => (
        <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
          ฿{record.total_frozen?.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const isFrozen = !!wallet?.is_frozen;
  const activeFreezeRecords = freezeHistory.filter(f => f.status === 'frozen');
  const activeFreezeRecord = activeFreezeRecords[0];
  const totalFrozenAllRecords = activeFreezeRecords.reduce((sum, r) => sum + (r.total_frozen || 0), 0);
  const hasFreezeActivity = isFrozen || activeFreezeRecords.length > 0;
  const needsUsdtAddress = hasFreezeActivity && !usdtAddress;
  const mainAvailable = isFrozen ? (wallet?.available_balance || 0) : (wallet?.balance || 0);

  return (
    <div className="w-full">
      <UiPageHeader icon={faWallet} title={t('wallet.title')} subtitle={t('wallet.desc')} />

      {/* USDT Address Popup */}
      {needsUsdtAddress && showUsdtPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full overflow-hidden">
            <div className="px-5 py-4" style={{ background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faWallet} className="text-white text-lg" />
                </div>
                <h3 className="text-white font-bold text-base">{t('wallet.usdtPopup.title')}</h3>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700 dark:text-slate-300 mb-4">{t('wallet.usdtPopup.description')}</p>
              <button
                onClick={() => navigate('/profile')}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm mb-2 transition-colors"
                style={{ background: `linear-gradient(135deg, ${theme.primary_color} 0%, ${theme.secondary_color} 100%)` }}
              >
                {t('wallet.usdtPopup.cta')}
              </button>
              <button
                onClick={() => setShowUsdtPopup(false)}
                className="w-full py-2 text-sm text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Frozen banner (v2 — money already deducted, informational) */}
      {!isFrozen && activeFreezeRecords.length > 0 && (
        <div className="mb-4 rounded-2xl border border-blue-300 dark:border-blue-800 overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'var(--theme-gradient-info)' }}>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <FontAwesomeIcon icon={faSnowflake} className="text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-base">{activeFreezeRecords.length > 1 ? t('wallet.frozenBanner2.titleMultiple', { count: activeFreezeRecords.length }) : t('wallet.frozenBanner2.title')}</h3>
              {activeFreezeRecord?.freeze_date && (
                <p className="text-blue-100 text-xs mt-0.5">
                  {t('wallet.frozenBanner2.frozenSince')} {new Date(activeFreezeRecord.freeze_date).toLocaleDateString('th-TH')}
                </p>
              )}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">{t('wallet.frozenBanner2.description')}</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner2.totalFrozen')}</p>
                <p className="text-base font-bold text-blue-700 dark:text-blue-400">฿{totalFrozenAllRecords.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner2.currentBalance')}</p>
                <p className="text-base font-bold text-green-700 dark:text-green-400">฿{(wallet?.balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            {activeFreezeRecords.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">{t('wallet.freezeBreakdown')}</p>
                {activeFreezeRecords.map((record, idx) => (
                  <div key={record.id} className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-200">{t('wallet.freezeNumber', { number: idx + 1 })} — {new Date(record.freeze_date).toLocaleDateString('th-TH')}</p>
                    <p className="text-sm font-bold text-blue-700 dark:text-blue-400 shrink-0">฿{(record.total_frozen || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Frozen banner (v1 legacy) */}
      {isFrozen && (
        <div className="mb-4 rounded-2xl border border-blue-300 dark:border-blue-800 overflow-hidden shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'var(--theme-gradient-info)' }}>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <FontAwesomeIcon icon={faSnowflake} className="text-white text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-base">{t('wallet.frozenBanner.title')}</h3>
              {wallet?.freeze_date && (
                <p className="text-blue-100 text-xs mt-0.5">
                  {t('wallet.frozenBanner.frozenSince')} {new Date(wallet.freeze_date).toLocaleDateString('th-TH')}
                </p>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">{t('wallet.frozenBanner.description')}</p>

            {/* Amounts grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner.frozenAmount')}</p>
                <p className="text-base font-bold text-blue-700 dark:text-blue-400">฿{(wallet?.frozen_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
              {wallet?.frozen_payin != null && (
                <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner.frozenPayin')}</p>
                  <p className="text-base font-bold text-blue-600 dark:text-blue-400">฿{(wallet.frozen_payin).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                </div>
              )}
              {wallet?.frozen_payout != null && wallet.frozen_payout > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner.frozenPayout')}</p>
                  <p className="text-base font-bold text-blue-600 dark:text-blue-400">฿{(wallet.frozen_payout).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                </div>
              )}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border border-green-100 dark:border-green-800">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">{t('wallet.frozenBanner.availableBalance')}</p>
                <p className="text-base font-bold text-green-700 dark:text-green-400">฿{(wallet?.available_balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>

            {activeFreezeRecords.length > 1 && (
              <div className="space-y-2 mb-3">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">{t('wallet.freezeBreakdownCount', { count: activeFreezeRecords.length })}</p>
                {activeFreezeRecords.map((record, idx) => (
                  <div key={record.id} className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-blue-100 dark:border-slate-700 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-200">{t('wallet.freezeNumber', { number: idx + 1 })} — {new Date(record.freeze_date).toLocaleDateString('th-TH')}</p>
                    <p className="text-sm font-bold text-blue-700 dark:text-blue-400 shrink-0">฿{(record.total_frozen || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-blue-500 dark:text-blue-400">{t('wallet.frozenBanner.contactAdmin')}</p>
          </div>
        </div>
      )}

      {/* Total Balance Summary */}
      <div className="mb-4 rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm px-5 py-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: 'var(--theme-accent-gradient)' }}>
            <FontAwesomeIcon icon={faVault} />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-medium">{t('wallet.balance')} {t('wallet.mainWallet')} + {t('wallet.payoutWallet')}</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-slate-100">
              ฿{(mainAvailable + (wallet?.payout_balance || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 ml-auto">
          <div className="text-right">
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('wallet.mainWallet')}</p>
            <p className="text-sm font-bold text-gray-700 dark:text-slate-200">
              ฿{mainAvailable.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-gray-300 dark:text-slate-600 self-center text-sm">+</div>
          <div className="text-right">
            <p className="text-xs text-gray-400 dark:text-slate-500">{t('wallet.payoutWallet')}</p>
            <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
              ฿{(wallet?.payout_balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Wallet Cards — balance trio */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-5">
        {/* Main Wallet */}
        <div className="relative overflow-hidden rounded-3xl p-6 bg-white dark:bg-slate-800 ring-1 ring-gray-100 dark:ring-slate-700 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
          <div className="pointer-events-none absolute -top-16 -right-12 h-40 w-40 rounded-full blur-3xl opacity-60"
            style={{ background: isFrozen ? 'rgba(59,130,246,0.18)' : 'var(--theme-primary-light)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className={`h-11 w-11 rounded-2xl flex items-center justify-center text-lg ring-1 ${isFrozen ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 ring-blue-100 dark:ring-blue-900/40' : 'bg-primary/10 text-primary ring-primary/15'}`}>
                <FontAwesomeIcon icon={isFrozen ? faSnowflake : faWallet} />
              </div>
              <div className="min-w-0">
                <h2 className="font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2 text-[15px] leading-tight">
                  {t('wallet.mainWallet')}
                  {isFrozen && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-semibold">
                      <FontAwesomeIcon icon={faSnowflake} className="text-[9px]" />
                      Frozen
                    </span>
                  )}
                </h2>
                <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{t('wallet.mainWalletDesc')}</p>
              </div>
            </div>

            <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-500">{t('wallet.balance')}</p>
            <p className={`mt-1 text-[28px] leading-none font-black tracking-tight ${isFrozen ? 'text-blue-500' : 'text-gray-800 dark:text-slate-100'}`}
              style={{ fontVariantNumeric: 'tabular-nums' }}>
              {bahtFmt(mainAvailable)}
            </p>
            {isFrozen && wallet?.frozen_payin != null && (
              <p className="mt-1.5 text-xs text-blue-500 dark:text-blue-400 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faSnowflake} className="text-[10px]" />
                {t('wallet.frozenBanner.frozenPayin')}: {bahtFmt(wallet.frozen_payin)}
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="rounded-xl bg-green-50 dark:bg-green-900/20 ring-1 ring-inset ring-green-100 dark:ring-green-900/30 px-3 py-2.5">
                <p className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1.5 mb-1"><FontAwesomeIcon icon={faMoneyBill} className="text-[10px]" /> {t('wallet.income')}</p>
                <p className="text-base font-bold text-green-700 dark:text-green-300 tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{bahtFmt(wallet?.total_credit || 0)}</p>
              </div>
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 ring-1 ring-inset ring-red-100 dark:ring-red-900/30 px-3 py-2.5">
                <p className="text-[11px] font-medium text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-1"><FontAwesomeIcon icon={faWallet} className="text-[10px]" /> {t('wallet.expense')}</p>
                <p className="text-base font-bold text-red-700 dark:text-red-300 tracking-tight truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{bahtFmt(wallet?.total_debit || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payout Wallet */}
        <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-lg ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          style={{ background: 'var(--theme-accent-gradient)' }}>
          <div className="pointer-events-none absolute -top-14 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-36 w-36 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <div className="flex flex-col gap-3 mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner shrink-0">
                  <FontAwesomeIcon icon={faPiggyBank} />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-[15px] leading-tight">{t('wallet.payoutWallet')}</h2>
                  <p className="text-[11px] text-white/65 truncate">{t('wallet.payoutWalletDesc')}</p>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-white/15 hover:bg-white/25 ring-1 ring-white/25 backdrop-blur transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
                  {t('wallet.transferIn')}
                </button>
                <button
                  onClick={() => { resetTopup(); setShowTopupModal(true); }}
                  className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-white/15 hover:bg-white/25 ring-1 ring-white/25 backdrop-blur transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-[11px]" />
                  {t('wallet.topup.button')}
                </button>
                {(wallet?.payout_balance || 0) > 0 && (
                  <button
                    onClick={() => setShowTransferToMainModal(true)}
                    className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-black/15 hover:bg-black/25 ring-1 ring-white/20 backdrop-blur transition-colors"
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} className="text-[11px]" />
                    {t('wallet.returnToMain')}
                  </button>
                )}
              </div>
            </div>

            <p className="text-[11px] font-medium uppercase tracking-wider text-white/65">{t('wallet.payoutWalletBalance')}</p>
            <p className="mt-1 text-[28px] leading-none font-black tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>{bahtFmt(wallet?.payout_balance || 0)}</p>
            {isFrozen && wallet?.frozen_payout != null && wallet.frozen_payout > 0 && (
              <p className="mt-1.5 text-xs text-white/75 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faSnowflake} className="text-[10px]" />
                {t('wallet.frozenBanner.frozenPayout')}: {bahtFmt(wallet.frozen_payout)}
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-5">
              <GlassStat icon={faMoneyBill} label={t('wallet.transferIn')} value={bahtFmt(wallet?.total_payout_credit || 0)} />
              <GlassStat icon={faWallet} label={t('wallet.payoutOut')} value={bahtFmt(wallet?.total_payout_debit || 0)} />
            </div>
          </div>
        </div>

        {/* Safe Wallet */}
        <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-lg ring-1 ring-white/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          style={{ background: 'var(--theme-secondary-gradient)' }}>
          <div className="pointer-events-none absolute -top-14 -right-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-36 w-36 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur flex items-center justify-center text-lg shadow-inner">
                  <FontAwesomeIcon icon={faVault} />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-[15px] leading-tight">Safe Wallet</h2>
                  <p className="text-[11px] text-white/65 truncate">{t('wallet.safeWalletSubtitle')}</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/safe-wallet')}
                className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-white/15 hover:bg-white/25 ring-1 ring-white/25 backdrop-blur transition-colors shrink-0"
              >
                <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
                {t('wallet.safeManage')}
              </button>
            </div>

            <p className="text-[11px] font-medium uppercase tracking-wider text-white/65">USDT Balance</p>
            <p className="mt-1 text-[28px] leading-none font-black tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {parseFloat(wallet?.safe_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
              <span className="text-base font-bold text-white/70 ml-1.5">USDT</span>
            </p>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <GlassStat label={t('wallet.safeDeposit')} value={`${parseFloat(wallet?.total_safe_credit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} USDT`} />
              <GlassStat label="Settlement" value={`${parseFloat(wallet?.total_safe_debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} USDT`} />
            </div>
          </div>
        </div>
      </div>

      {/* Auto Transfer — full-width settings strip */}
      <div className="relative overflow-hidden rounded-3xl p-6 bg-white dark:bg-slate-800 ring-1 ring-gray-100 dark:ring-slate-700 shadow-lg mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15 flex items-center justify-center text-lg">
              <FontAwesomeIcon icon={faRotate} />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-800 dark:text-slate-100 text-[15px] leading-tight">Auto Transfer</h2>
              <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate">{t('wallet.autoTransfer.subtitle')}</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={autoTransferSettings?.auto_transfer_enabled || false}
              onChange={async (e) => {
                const enabled = e.target.checked;
                setAutoTransferSettings(prev => prev ? { ...prev, auto_transfer_enabled: enabled } : null);
                try {
                  await merchantApi.put('/merchants/wallet/auto-transfer-settings', { auto_transfer_enabled: enabled });
                  toast.success(enabled ? t('wallet.autoTransfer.enabled') : t('wallet.autoTransfer.disabled'));
                  if (enabled) loadAutoTransferLogs();
                } catch {
                  toast.error(t('common.error'));
                  setAutoTransferSettings(prev => prev ? { ...prev, auto_transfer_enabled: !enabled } : null);
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
        </div>

        {autoTransferSettings?.auto_transfer_enabled ? (
          <>
            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
              <UiInput
                label={t('wallet.autoTransfer.thresholdLabel')}
                type="number"
                value={editThreshold}
                onChange={(e) => setEditThreshold(e.target.value)}
                placeholder={t('wallet.autoTransfer.thresholdPlaceholder')}
                min="0"
              />
              <UiInput
                label={t('wallet.autoTransfer.maxPayoutLabel')}
                type="number"
                value={editMaxPayout}
                onChange={(e) => setEditMaxPayout(e.target.value)}
                placeholder={t('wallet.autoTransfer.maxPayoutPlaceholder')}
                min="0"
              />
              <UiButton onClick={handleSaveAutoTransfer} loading={savingAutoTransfer} className="justify-center lg:px-6">
                {savingAutoTransfer ? t('wallet.autoTransfer.saving') : t('wallet.autoTransfer.save')}
              </UiButton>
            </div>
            <div className="mt-3 rounded-xl bg-primary/5 ring-1 ring-inset ring-primary/10 px-4 py-3 text-xs text-gray-600 dark:text-slate-300">
              <p>{t('wallet.autoTransfer.thresholdDesc', { threshold: Number(editThreshold || 0).toLocaleString() })}</p>
              <p className="mt-1">{t('wallet.autoTransfer.maxPayoutDesc', { max: Number(editMaxPayout || 0).toLocaleString() })}</p>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400 dark:text-slate-500 mt-3">{t('wallet.autoTransfer.disabledHint')}</p>
        )}
      </div>

      {/* Payout Transfers History */}
      <div className="mb-6">
        <DataTable<PayoutTransfer>
          data={payoutTransfers}
          columns={transferColumns}
          loading={loadingTransfers}
          emptyMessage={t('wallet.noTransferHistory')}
          emptyIcon={<FontAwesomeIcon icon={faExchangeAlt} className="text-5xl text-gray-300 dark:text-slate-600" />}
          keyExtractor={(transfer) => transfer.id}
          pagination={{
            enabled: true,
            pageSize: 5,
            pageSizeOptions: [5, 10, 20],
            showPageSizeSelector: true,
            showInfo: true,
            showFirstLast: false,
          }}
          headerContent={
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faExchangeAlt} className="text-indigo-500" />
              {t('wallet.transferHistory')}
            </h2>
          }
          hoverable
        />
      </div>

      {/* Payout Top-up History (ประวัติการเติม Payout ของลูกค้า) */}
      <div className="mb-6">
        <DataTable<MerchantTopup>
          data={payoutTopupsList}
          columns={topupColumns}
          loading={loadingTopupsList}
          emptyMessage="-"
          emptyIcon={<FontAwesomeIcon icon={faPiggyBank} className="text-5xl text-gray-300 dark:text-slate-600" />}
          keyExtractor={(r) => r.id}
          pagination={{
            enabled: true,
            pageSize: 5,
            pageSizeOptions: [5, 10, 20],
            showPageSizeSelector: true,
            showInfo: true,
            showFirstLast: false,
          }}
          headerContent={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faPiggyBank} className="text-emerald-500" />
                {t('wallet.topup.historyTitle')}
              </h2>
              {/* ยอดรวมจาก backend ครบทุกแถว (backend เก่าไม่มี summary → ซ่อน) */}
              {topupSummary && topupSummary.paid_count > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 font-semibold">
                    {t('wallet.topup.summaryPaid', { count: topupSummary.paid_count })} · {bahtFmt(topupSummary.total_amount)}
                  </span>
                  <span className="rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-2.5 py-1 font-semibold">
                    {t('wallet.topup.summaryFee')} {bahtFmt(topupSummary.total_fee)}
                  </span>
                  <span className="rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 px-2.5 py-1 font-semibold">
                    {t('wallet.topup.toPayout')} {bahtFmt(topupSummary.total_net)}
                  </span>
                </div>
              )}
            </div>
          }
          hoverable
        />
      </div>

      {/* Auto Transfer Logs */}
      {autoTransferSettings?.auto_transfer_enabled && (
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faRotate} className="text-indigo-500" />
                Auto Transfer Logs
              </h2>
              <button
                onClick={loadAutoTransferLogs}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 rounded-lg text-xs font-medium transition-colors"
              >
                <FontAwesomeIcon icon={faRotate} className="mr-1" />
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              {loadingAutoTransfer ? (
                <div className="p-8 text-center text-gray-400 dark:text-slate-500">{t('common.loading')}</div>
              ) : autoTransferLogs.length === 0 ? (
                <div className="p-8 text-center text-gray-400 dark:text-slate-500">{t('wallet.autoTransfer.noLogs')}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400">{t('common.status')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-slate-400">{t('wallet.autoTransfer.colAmount')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400">{t('wallet.detail')}</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-slate-400">Main Wallet</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-slate-400">Payout Wallet</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400">{t('common.date')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {autoTransferLogs.map((log: any) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          {log.status === 'transferred' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              <FontAwesomeIcon icon={faCheck} /> {t('wallet.autoTransfer.statusTransferred')}
                            </span>
                          )}
                          {log.status === 'skipped_below_threshold' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                              <FontAwesomeIcon icon={faClock} /> {t('wallet.autoTransfer.statusBelowThreshold')}
                            </span>
                          )}
                          {log.status === 'skipped_max_reached' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                              <FontAwesomeIcon icon={faCheck} /> {t('wallet.autoTransfer.statusMaxReached')}
                            </span>
                          )}
                          {log.status === 'failed' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                              <FontAwesomeIcon icon={faXmark} /> {t('wallet.autoTransfer.statusFailed')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {log.amount > 0 ? (
                            <span className="font-bold text-green-600 dark:text-green-400">฿{Number(log.amount).toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-400 dark:text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500 dark:text-slate-400">{log.reason}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-xs">
                          <div className="text-gray-500 dark:text-slate-400">
                            ฿{Number(log.main_balance_before).toLocaleString()}
                            {log.amount > 0 && (
                              <span className="text-gray-400 dark:text-slate-500"> → ฿{Number(log.main_balance_after).toLocaleString()}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-xs">
                          <div className="text-gray-500 dark:text-slate-400">
                            ฿{Number(log.payout_balance_before).toLocaleString()}
                            {log.amount > 0 && (
                              <span className="text-indigo-600 dark:text-indigo-400 font-medium"> → ฿{Number(log.payout_balance_after).toLocaleString()}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">
                          {new Date(log.created_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transactions History */}
      <div className="mb-6">
        <DataTable<Transaction>
          data={transactions}
          columns={transactionColumns}
          loading={false}
          emptyMessage={t('wallet.noTransactions')}
          emptyIcon={<FontAwesomeIcon icon={faReceipt} className="text-5xl text-gray-300 dark:text-slate-600" />}
          keyExtractor={(tx) => tx.id}
          pagination={{
            enabled: true,
            pageSize: 10,
            pageSizeOptions: [5, 10, 20],
            showPageSizeSelector: true,
            showInfo: true,
            showFirstLast: false,
          }}
          headerContent={
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faHistory} className="text-purple-500" />
              {t('wallet.withdrawalHistory')}
            </h2>
          }
          hoverable
        />
      </div>

      {/* Freeze History (บัญชีถูก Freeze) — อยู่ใต้กระเป๋าเงิน */}
      <div className="mb-6">
        <DataTable<FreezeHistory>
          data={freezeHistory}
          columns={freezeHistoryColumns}
          loading={loadingFreezeHistory}
          emptyMessage={t('wallet.noFreezeHistory')}
          emptyIcon={<FontAwesomeIcon icon={faSnowflake} className="text-5xl text-gray-300 dark:text-slate-600" />}
          keyExtractor={(record) => record.id}
          pagination={{
            enabled: true,
            pageSize: 5,
            pageSizeOptions: [5, 10, 20],
            showPageSizeSelector: true,
            showInfo: true,
            showFirstLast: false,
          }}
          headerContent={
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faSnowflake} className="text-blue-500" />
              {t('wallet.freezeHistory')}
            </h2>
          }
          hoverable
        />
      </div>

      {/* Slip Modal */}
      <UiModal
        open={showSlipModal}
        onClose={() => setShowSlipModal(false)}
        title={t('common.transferSlip')}
        icon={faReceipt}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
      >
        <div className="text-center">
          {selectedSlipUrl ? (
            <img
              src={selectedSlipUrl}
              alt="Slip"
              className="max-w-full max-h-[70vh] rounded-lg border border-gray-200 dark:border-slate-700 mx-auto"
              onError={() => toast.error(t('common.cannotLoadImage'))}
            />
          ) : (
            <p className="text-gray-500 dark:text-slate-400">{t('common.noSlipData')}</p>
          )}
        </div>
      </UiModal>

      {/* Transfer to Payout Modal */}
      <UiModal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={t('wallet.transferToPayout')}
        icon={faExchangeAlt}
        iconColor="text-indigo-600"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowTransferModal(false)} disabled={transferring} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleTransferToPayout}
              loading={transferring}
              disabled={!transferAmount || parseFloat(transferAmount) <= 0}
              className="flex-1 justify-center"
            >
              {transferring ? t('wallet.transferring') : t('wallet.confirmTransfer')}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-slate-400">{t('wallet.transferDesc')}</p>

        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">{t('wallet.mainWallet')}</p>
            <p className="text-lg font-bold text-gray-800 dark:text-slate-100">฿{mainAvailable.toLocaleString()}</p>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="text-indigo-500 text-xl" />
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-slate-400">{t('wallet.payoutWallet')}</p>
            <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">฿{(wallet?.payout_balance || 0).toLocaleString()}</p>
          </div>
        </div>

        <div>
          <UiInput
            label={t('wallet.transferAmountLabel')}
            prefix="฿"
            type="number"
            step="0.01"
            min="1"
            max={isFrozen ? wallet?.available_balance : wallet?.balance || 0}
            value={transferAmount}
            onChange={(e) => setTransferAmount(e.target.value)}
            placeholder="0.00"
            className="text-lg font-semibold"
          />
          <div className="flex gap-2 mt-2">
            {[25, 50, 75].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setTransferAmount(String(Math.floor(mainAvailable * pct / 100)))}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTransferAmount(String(mainAvailable))}
              className="px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-400 rounded-lg text-xs font-medium transition-colors"
            >
              {t('common.selectAll')}
            </button>
          </div>
        </div>

        <UiInput
          label={t('wallet.noteOptional')}
          type="text"
          value={transferNote}
          onChange={(e) => setTransferNote(e.target.value)}
          placeholder={t('wallet.notePlaceholder')}
          maxLength={100}
        />
      </UiModal>

      {/* Transfer Payout to Main Modal */}
      <UiModal
        open={showTransferToMainModal}
        onClose={() => setShowTransferToMainModal(false)}
        title={t('wallet.transferToMain')}
        icon={faExchangeAlt}
        iconColor="text-orange-500"
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowTransferToMainModal(false)} disabled={transferringToMain} className="flex-1 justify-center">
              {t('common.cancel')}
            </UiButton>
            <UiButton
              onClick={handleTransferToMain}
              loading={transferringToMain}
              disabled={!transferToMainAmount || parseFloat(transferToMainAmount) <= 0}
              className="flex-1 justify-center"
            >
              {transferringToMain ? t('wallet.transferringToMain') : t('wallet.confirmTransferBack')}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm text-gray-500 dark:text-slate-400">{t('wallet.transferToMainDesc')}</p>

        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400">{t('wallet.payoutWallet')}</p>
            <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">฿{(wallet?.payout_balance || 0).toLocaleString()}</p>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="text-orange-500 text-xl" />
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-slate-400">{t('wallet.mainWallet')}</p>
            <p className="text-lg font-bold text-gray-800 dark:text-slate-100">฿{(wallet?.balance || 0).toLocaleString()}</p>
          </div>
        </div>

        <div>
          <UiInput
            label={t('wallet.transferToMainAmount')}
            prefix="฿"
            type="number"
            step="0.01"
            min="1"
            max={wallet?.payout_balance || 0}
            value={transferToMainAmount}
            onChange={(e) => setTransferToMainAmount(e.target.value)}
            placeholder="0.00"
            className="text-lg font-semibold"
          />
          <div className="flex gap-2 mt-2">
            {[25, 50, 75].map(pct => (
              <button
                key={pct}
                type="button"
                onClick={() => setTransferToMainAmount(String(Math.floor((wallet?.payout_balance || 0) * pct / 100)))}
                className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-colors"
              >
                {pct}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTransferToMainAmount(String(wallet?.payout_balance || 0))}
              className="px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 text-orange-700 dark:text-orange-400 rounded-lg text-xs font-medium transition-colors"
            >
              {t('common.selectAll')}
            </button>
          </div>
        </div>

        <UiInput
          label={t('wallet.noteOptional')}
          type="text"
          value={transferToMainNote}
          onChange={(e) => setTransferToMainNote(e.target.value)}
          placeholder={t('wallet.transferToMainNotePlaceholder')}
          maxLength={200}
        />
      </UiModal>

      {/* Payout Top-up Modal (เติมเงินเข้า payout ด้วยสลิป) */}
      <UiModal
        open={showTopupModal}
        onClose={resetTopup}
        title={t('wallet.topup.title')}
        icon={faPiggyBank}
        iconColor="text-emerald-600"
        size="md"
        scrollable
        footer={
          topupPhase === 1 ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={resetTopup} disabled={topupCreating} className="flex-1 justify-center">
                {t('common.cancel')}
              </UiButton>
              <UiButton
                onClick={handleCreateTopup}
                loading={topupCreating}
                disabled={!topupAmount || parseFloat(topupAmount) <= 0 || !topupBankName || !topupBankNumber}
                className="flex-1 justify-center"
              >
                {t('wallet.topup.next')}
              </UiButton>
            </div>
          ) : (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setTopupPhase(1)} disabled={topupUploading} className="flex-1 justify-center">
                {t('wallet.topup.back')}
              </UiButton>
              <UiButton onClick={handleUploadTopupSlip} loading={topupUploading} disabled={!topupSlip} className="flex-1 justify-center">
                {topupUploading ? t('wallet.topup.verifying') : t('wallet.topup.confirmSlip')}
              </UiButton>
            </div>
          )
        }
      >
        {topupPhase === 1 ? (
          <>
            <p className="text-sm text-gray-500 dark:text-slate-400">{t('wallet.topup.desc')}</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{t('wallet.topup.senderBank')}</label>
              <select
                value={topupBankName}
                onChange={(e) => setTopupBankName(e.target.value)}
                className="w-full rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-gray-800 dark:text-slate-100"
              >
                <option value="">{t('wallet.topup.selectBank')}</option>
                {bankList.filter(b => b.isActive).map(b => (
                  <option key={b.shortName} value={b.shortName}>{b.shortName} - {b.bankName}</option>
                ))}
              </select>
            </div>
            <UiInput label={t('wallet.topup.senderAccount')} type="text" value={topupBankNumber} onChange={(e) => setTopupBankNumber(e.target.value)} placeholder="1234567890" />
            <UiInput label={t('wallet.topup.amountLabel')} prefix="฿" type="number" step="0.01" min="1" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} placeholder="0.00" className="text-lg font-semibold" />
            {/* preview fee แบบประมาณการ (สูตรเดียวกับ server) — ยอดจริงคิดใหม่จากสลิปตอน settle */}
            {feePercentage != null && parseFloat(topupAmount) > 0 && (() => {
              const amt = parseFloat(topupAmount);
              const fee = Math.round(amt * (feePercentage / 100) * 100) / 100;
              const net = Math.round((amt - fee) * 100) / 100;
              return (
                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.amountToTransfer')}</p>
                      <p className="text-sm font-bold text-gray-800 dark:text-slate-100">฿{amt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.fee')}</p>
                      <p className="text-sm font-bold text-amber-600 dark:text-amber-400">-฿{fee.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.toPayout')}</p>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">฿{net.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-gray-400 dark:text-slate-500 text-center">{t('wallet.topup.previewNote', { rate: feePercentage })}</p>
                </div>
              );
            })()}
            <UiInput label={t('wallet.noteOptional')} type="text" value={topupNote} onChange={(e) => setTopupNote(e.target.value)} placeholder={t('wallet.notePlaceholder')} maxLength={100} />
            {topupError && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                <FontAwesomeIcon icon={faXmark} className="mt-0.5" />{topupError}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.amountToTransfer')}</p>
                <p className="text-base font-bold text-gray-800 dark:text-slate-100">฿{Number(topupResult?.amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.fee')}</p>
                <p className="text-base font-bold text-amber-600 dark:text-amber-400">-฿{Number(topupResult?.fee_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">{t('wallet.topup.toPayout')}</p>
                <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">฿{Number(topupResult?.net_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">{t('wallet.topup.transferToThisAccount')}</p>
              <div className="flex items-center gap-2">
                {(() => {
                  const key = String(topupResult?.bank_info?.bank_code || '').toUpperCase().replace('CIMBT', 'CIMB').replace('UOBT', 'UOB').replace('LH BANK', 'LHB') as keyof typeof bankLogos;
                  const logo = bankLogos[key];
                  return logo ? <img src={logo.icon} alt={String(key)} className="w-7 h-7 rounded-full object-contain bg-white" /> : null;
                })()}
                <p className="font-bold text-gray-800 dark:text-slate-100">{topupResult?.bank_info?.name}</p>
              </div>
              <div className="flex items-center justify-between mt-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2">
                <span className="font-mono font-bold text-gray-800 dark:text-slate-100">{topupResult?.bank_info?.account_number}</span>
                <button type="button" onClick={() => copyTopupText(topupResult?.bank_info?.account_number || '')} className="text-indigo-500 hover:text-indigo-600">
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{t('wallet.topup.attachSlip')}</label>
              {topupSlip ? (
                <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600">
                  <img src={topupSlip} alt="slip" className="w-full max-h-60 object-contain bg-gray-50 dark:bg-slate-800" />
                  <button type="button" onClick={() => setTopupSlip('')} className="absolute top-2 right-2 bg-black/50 text-white rounded-full w-7 h-7 flex items-center justify-center">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl p-6 cursor-pointer hover:border-indigo-400 transition-colors">
                  <FontAwesomeIcon icon={faUpload} className="text-2xl text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-slate-400">{t('wallet.topup.tapToSelect')}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleTopupSlipChange} />
                </label>
              )}
            </div>
            {topupError && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                <FontAwesomeIcon icon={faXmark} className="mt-0.5" />{topupError}
              </div>
            )}
          </>
        )}
      </UiModal>
    </div>
  );
};

export default WalletPage;
